// src/oura/merchant/drawers/ChangeRequestDrawer.tsx
//
// "Ask for a change": the one way a merchant starts a request (YW, 28 Sep
// 2026). Opened from Work, and from a request's "Ask a follow-up" (`about`),
// which quotes that request and sends the words as a follow-up on the same
// request (#4930): a message in its conversation, not a new request. What
// they write, and any photos or files they add, goes to their account
// manager, named from the payload (`preparedBy`), at the address the payload
// gives (`contactEmail`). Neither is written here. A request that went
// through shows at once in "Your requests": the service announces it
// (requestChange, or followUpChangeRequest for a follow-up).
//
// Each file uploads the moment it is chosen, so Send only carries the keys
// the uploads returned. A file over 20 MB, or not a photo or a PDF, is
// refused with the reason and the text stays.
//
// The draft lives in this component, which stays mounted while the drawer is
// shut: closing keeps it (and says so), a failed send keeps it (text and
// files) with the reason under it, and only a send that went through clears
// it. Each draft belongs to what it is about, a new request or a follow-up
// on one request: opening another keeps the first aside, so its words and
// files never go out under another request. Focus lands in the text box:
// typing is the only thing to do here.
//
// The Work page shows the button only where `canRequestChange()` says it can
// send: on the sample store, or on live behind VITE_ENABLE_OURA_CHANGE_REQUEST.

import { useId, useLayoutEffect, useRef, useState } from "react";
import { FileTextIcon, ImageIcon, Loader2Icon, MessageSquarePlusIcon, PaperclipIcon, SendIcon, XIcon } from "lucide-react";
import { followUpChangeRequest, requestChange, uploadFile, UPLOAD_ACCEPT, UPLOAD_MAX_BYTES } from "@service/aeo/clientReview";
import { useSnackbar } from "@hook/use-snackbar";
import { useWritesPaused } from "@context/maintenance";
import { useViewAs } from "@hook/use-view-as";
import { AREA_TEXT, Drawer, DrawerBlock, DrawerFoot, DrawerHead, DrawerQuote, FOCUS_RING, LATE_TEXT, PillButton, TAP_TARGET, TYPE, useDrawerFocusClaim } from "@oura/ui";
import { docDate } from "@oura/docs";
import { cn } from "../../lib/cn";

const FIELD = cn(
    TYPE.tb,
    "block w-full resize-y rounded-xl border-0 bg-gray-50 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

/** The server refuses a note over this (`note_too_long`). */
const NOTE_MAX = 2000;
/** From here the count shows under the box, before it becomes a problem. */
const NOTE_WARN = 1800;

/** What the upload route takes: the same list as UPLOAD_ACCEPT. */
const ALLOWED_EXT = UPLOAD_ACCEPT.split(",").map((e) => e.trim().toLowerCase());

const count = (n: number) => n.toLocaleString("en-GB");

/** "340 KB", "1.2 MB". */
function sizeText(bytes: number): string {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
}

const extOf = (name: string) => {
    const dot = name.lastIndexOf(".");
    return dot >= 0 ? name.slice(dot).toLowerCase() : "";
};

interface Chosen {
    /** This drawer's own handle, so a removal during the upload wins over its result. */
    id: number;
    name: string;
    size: number;
    image: boolean;
    /** Set once the upload is back; Send carries it. */
    fileKey: string | null;
}

/** The request a follow-up is about: which one, when it was sent, and what it said. */
export interface ChangeAbout {
    id: string;
    at: string;
    note: string;
}

/** A draft kept aside while another request's is open. */
interface Draft {
    note: string;
    touched: boolean;
    files: Chosen[];
}

/**
 * Staff viewing as the business may ask for them (3 Nov 2026): a change is often
 * taken on a call. It goes as the business's own request, never as staff (ops).
 */
const VIEW_AS_SEND_NOTE = "You are viewing as this business: this is sent as them.";

export function ChangeRequestDrawer({
    open,
    onClose,
    about,
    businessId,
    manager,
    email,
    viewer,
    smallChangeInPlan,
    smallChangeUsedAt = null,
    inline = false,
    cancelable = false,
}: {
    open: boolean;
    onClose: () => void;
    /** A follow-up: the request it is about. */
    about?: ChangeAbout | null;
    /** The merchant's own business, where the files upload. */
    businessId: string;
    /** The client's account manager from the payload, or null when nobody is assigned. */
    manager: string | null;
    /** Where the request lands (the payload's `contactEmail`), or null when the server does not send one. */
    email: string | null;
    /** Who is asking: the signed-in merchant. */
    viewer: string;
    /** The contract carries G5, 1 small change each calendar month. Without it the plan note says nothing about one. */
    smallChangeInPlan: boolean;
    /** When this month's small change was sent, if it was: the sheet says it is used before anything is written. */
    smallChangeUsedAt?: string | null;
    /** In the account manager card rather than a drawer (16 Oct 2026): the same form, always open. */
    inline?: boolean;
    /** Inline, a new request offers Cancel (or Close once sent) too: Our work opens it from a button (27 Oct 2026). */
    cancelable?: boolean;
}) {
    const snackbar = useSnackbar();
    // Staff viewing as the merchant may send it for them, and are told so (3 Nov 2026).
    const { active: onBehalf } = useViewAs();
    const writesPaused = useWritesPaused();
    const noteId = useId();
    const hintId = useId();
    const fileInputId = useId();
    const fileRef = useRef<HTMLInputElement>(null);
    const nextId = useRef(0);
    /** Files removed by hand, so a late upload failure does not report on a file that is gone. */
    const removed = useRef(new Set<number>());

    const [note, setNote] = useState("");
    const [touched, setTouched] = useState(false);
    const [files, setFiles] = useState<Chosen[]>([]);
    const [fileProblems, setFileProblems] = useState<string[]>([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Sent: the sheet says so, as Decisions does, until they close it. */
    const [sentLine, setSentLine] = useState<string | null>(null);

    // What the draft on screen is about. Opening another puts this one aside
    // and brings that one's back (or an empty box), before anything paints.
    const context = about ? `follow-up:${about.id}` : "new";
    const shown = useRef(context);
    const aside = useRef(new Map<string, Draft>());
    useLayoutEffect(() => {
        if (shown.current === context) return;
        if (note || files.length > 0) aside.current.set(shown.current, { note, touched, files });
        else aside.current.delete(shown.current);
        const back = aside.current.get(context);
        aside.current.delete(context);
        shown.current = context;
        setNote(back?.note ?? "");
        setTouched(back?.touched ?? false);
        setFiles(back?.files ?? []);
        setFileProblems([]);
        setError(null);
    }, [context, note, touched, files]);
    /** A change to the files, wherever their draft is: on screen, or put aside while another is open. */
    const changeFiles = (change: (prev: Chosen[]) => Chosen[]) => {
        setFiles(change);
        aside.current.forEach((d) => {
            d.files = change(d.files);
        });
    };

    const text = note.trim();
    const over = text.length > NOTE_MAX;
    const uploading = files.some((f) => f.fileKey === null);

    const choose = (picked: FileList | null) => {
        if (!picked || picked.length === 0) return;
        const problems: string[] = [];
        const accepted: Array<{ chosen: Chosen; file: File }> = [];
        Array.from(picked).forEach((file) => {
            if (!ALLOWED_EXT.includes(extOf(file.name))) {
                problems.push(`${file.name} is not a photo or a PDF. Add a JPG, PNG, WebP or PDF.`);
            } else if (file.size > UPLOAD_MAX_BYTES) {
                problems.push(`${file.name} is over 20 MB. Add a smaller file.`);
            } else {
                const chosen: Chosen = { id: ++nextId.current, name: file.name, size: file.size, image: extOf(file.name) !== ".pdf", fileKey: null };
                accepted.push({ chosen, file });
            }
        });
        setFileProblems(problems);
        if (error) setError(null);
        if (accepted.length === 0) return;
        setFiles((prev) => [...prev, ...accepted.map((a) => a.chosen)]);
        const where = shown.current;
        accepted.forEach(({ chosen, file }) => {
            uploadFile(businessId, file)
                .then((up) => {
                    // A file removed while it uploaded is no longer in the list, so this finds nothing.
                    changeFiles((prev) => prev.map((f) => (f.id === chosen.id ? { ...f, fileKey: up.fileKey } : f)));
                })
                .catch(() => {
                    if (removed.current.has(chosen.id)) return;
                    changeFiles((prev) => prev.filter((f) => f.id !== chosen.id));
                    // Said only where it was chosen; a draft put aside just loses the file.
                    if (shown.current === where) setFileProblems((prev) => [...prev, `${chosen.name} did not upload. Try again.`]);
                });
        });
    };

    const remove = (id: number) => {
        removed.current.add(id);
        setFiles((prev) => prev.filter((f) => f.id !== id));
        // The remove button is gone, so focus goes back to the file button.
        fileRef.current?.focus();
    };

    const send = async () => {
        if (!text || over || uploading || sending || writesPaused) return;
        setSending(true);
        setError(null);
        // Refused files were seen already; the send's own outcome is what shows now.
        setFileProblems([]);
        try {
            const fileKeys = files.map((f) => f.fileKey).filter((k): k is string => !!k);
            // A follow-up is a message on the request it is about (#4930); anything else is a new request.
            if (about) await followUpChangeRequest(about.id, { note: text, ...(fileKeys.length ? { fileKeys } : {}) });
            else await requestChange({ note: text, requestedBy: viewer, ...(fileKeys.length ? { fileKeys } : {}) });
            setNote("");
            setTouched(false);
            setFiles([]);
            setFileProblems([]);
            setSentLine(manager ? `Sent to ${manager}. ${manager} replies by email.` : "Sent. We reply by email.");
        } catch (err) {
            const e = err as { code?: string | null; status?: number | null } | null;
            setError(
                e?.code === "note_too_long"
                    ? "Keep it to 2,000 characters or fewer. It is still here."
                    : e?.code === "file_not_yours"
                        ? "One of the files could not be attached. Remove it, add it again, then send."
                        : e?.status === 404
                          ? "This request is no longer here. Your words are still here."
                          : "Your request did not send. It is still here. Try again.",
            );
        } finally {
            setSending(false);
        }
    };

    // The words under the box: over the limit, the count, or what is missing.
    const hint = over
        ? { text: `${count(text.length)} characters. Keep it to ${count(NOTE_MAX)} or fewer.`, problem: true }
        : text.length >= NOTE_WARN
            ? { text: `${count(text.length)} of ${count(NOTE_MAX)} characters.`, problem: false }
            : !text && (touched || files.length > 0)
                ? { text: "Say what you'd like changed.", problem: false }
                : null;

    const uploadingCount = files.filter((f) => f.fileKey === null).length;
    // Closing keeps the draft; say so, since nothing on the page does.
    const dismiss = () => {
        setSentLine(null);
        onClose();
        if (text || files.length > 0) snackbar.showSuccess("Kept. Your request is not sent yet.");
    };
    const aboutDay = about ? docDate(about.at) : null;

    const block = (
        <ChangeBlock
            noteId={noteId}
            hintId={hintId}
            fileInputId={fileInputId}
            note={note}
            over={over}
            hint={hint}
            files={files}
            fileProblems={fileProblems}
            error={error}
            fileRef={fileRef}
            followUp={inline && Boolean(about)}
            smallChangeLine={
                inline && about
                    ? null
                    : smallChangeInPlan
                    ? smallChangeUsedAt
                        ? `You used this month's small change on ${docDate(smallChangeUsedAt) ?? "an earlier day"}. ${manager ? `${manager} quotes` : "We quote"} anything new before it starts.`
                        : `1 change under 30 minutes each calendar month is part of your plan. Anything bigger, ${manager ? `${manager} quotes` : "we quote"} before it starts.`
                    : null
            }
            onNote={(value) => {
                setNote(value);
                setTouched(true);
                // In the card, the next request starts as soon as they type.
                if (inline) setSentLine(null);
                if (error) setError(null);
            }}
            onChoose={choose}
            onRemove={remove}
            onSubmit={() => void send()}
            inline={inline}
        />
    );

    if (inline) {
        return (
            <div className="space-y-3">
                {block}
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <span aria-live="polite" className={cn(TYPE.tc, "mr-auto", sentLine ? "text-emerald-700" : "text-gray-500")}>
                        {sentLine ?? (uploadingCount > 0 ? `Uploading ${uploadingCount} ${uploadingCount === 1 ? "file" : "files"}` : onBehalf ? VIEW_AS_SEND_NOTE : null)}
                    </span>
                    {/* Secondary left, primary last on the right. */}
                    {(about || cancelable) && (
                        <PillButton variant="secondary" onClick={onClose}>
                            {sentLine && !about ? "Close" : "Cancel"}
                        </PillButton>
                    )}
                    <PillButton writes variant="primary" icon={SendIcon} busy={sending} disabled={!text || over || uploading} onClick={() => void send()}>
                        {about ? "Send follow-up" : manager ? `Send to ${manager}` : "Send"}
                    </PillButton>
                </div>
            </div>
        );
    }

    return (
        <Drawer open={open} onClose={dismiss} width={520}>
            <DrawerHead
                lead={<MessageSquarePlusIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.people} />}
                title="Ask for a change"
                // No dot: a request has no state until it is sent.
                status={{ label: `To ${manager ?? "your account manager"}`, clock: email ? { text: email } : null }}
            />
            {about && (
                <DrawerQuote who={`About your request${aboutDay ? ` of ${aboutDay}` : ""}`}>
                    {/* 2 lines, not 1: a phone has no hover to show the rest. */}
                    <span className="line-clamp-2 block break-words" title={about.note}>
                        {about.note}
                    </span>
                </DrawerQuote>
            )}
            {block}
            <DrawerFoot
                message={
                    sentLine
                        ? { tone: "success", text: sentLine }
                        : uploadingCount > 0
                          ? { tone: "info", text: `Uploading ${uploadingCount} ${uploadingCount === 1 ? "file" : "files"}` }
                          : onBehalf
                            ? { tone: "info", text: VIEW_AS_SEND_NOTE }
                            : null
                }
                first={
                    sentLine ? (
                        <PillButton
                            variant="primary"
                            onClick={() => {
                                setSentLine(null);
                                onClose();
                            }}
                        >
                            Done
                        </PillButton>
                    ) : (
                    <PillButton
                        variant="primary"
                        writes
                        icon={SendIcon}
                        busy={sending}
                        disabled={!text || over || uploading}
                        onClick={() => void send()}
                    >
                        {manager ? `Send to ${manager}` : "Send"}
                    </PillButton>
                    )
                }
            />
        </Drawer>
    );
}

/**
 * What they write and add: the box, the files and anything refused, then
 * what the plan includes. Its own component, so the box can claim focus
 * inside the drawer (useDrawerFocusClaim).
 */
function ChangeBlock({
    noteId,
    hintId,
    fileInputId,
    note,
    over,
    hint,
    files,
    fileProblems,
    error,
    fileRef,
    smallChangeLine,
    onNote,
    onChoose,
    onRemove,
    onSubmit,
    inline = false,
    followUp = false,
}: {
    noteId: string;
    hintId: string;
    fileInputId: string;
    note: string;
    over: boolean;
    hint: { text: string; problem: boolean } | null;
    files: Chosen[];
    fileProblems: string[];
    error: string | null;
    fileRef: React.RefObject<HTMLInputElement | null>;
    smallChangeLine: string | null;
    onNote: (value: string) => void;
    onChoose: (picked: FileList | null) => void;
    onRemove: (id: number) => void;
    onSubmit: () => void;
    inline?: boolean;
    /** In place under a request: the box adds to that request, and says so. */
    followUp?: boolean;
}) {
    const claim = useDrawerFocusClaim();
    const writesPaused = useWritesPaused();
    return (
        <DrawerBlock className={inline ? "!rounded-none !bg-transparent !p-0 !ring-0" : undefined}>
            <label htmlFor={noteId} className={cn(TYPE.tb, "m-0 block font-medium text-gray-900")}>
                {followUp ? "Your follow-up" : "What would you like changed?"}
            </label>
            <textarea
                ref={claim}
                id={noteId}
                rows={4}
                value={note}
                aria-invalid={over || undefined}
                aria-describedby={hint ? hintId : undefined}
                onChange={(e) => onNote(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
                placeholder={followUp ? "What else should we know? It is added to this request." :"For example: add our new Sunday hours to the contact page."}
                className={cn(FIELD, "mt-2")}
            />
            {/* The live region stays mounted and only its words change, so the hint is read when it first appears. */}
            <div aria-live="polite" aria-atomic="true">
                {hint && (
                    <p id={hintId} className={cn(TYPE.tc, "m-0 mt-2 tabular-nums", hint.problem ? LATE_TEXT : "text-gray-500")}>
                        {hint.text}
                    </p>
                )}
            </div>

            <div className="mt-3">
                <input
                    ref={fileRef}
                    id={fileInputId}
                    type="file"
                    multiple
                    accept={UPLOAD_ACCEPT}
                    disabled={writesPaused}
                    className="peer sr-only"
                    onChange={(e) => {
                        onChoose(e.target.files);
                        // Clear it, so choosing the same file again still fires.
                        e.target.value = "";
                    }}
                />
                <label
                    htmlFor={fileInputId}
                    className={cn(
                        TYPE.tc,
                        "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full bg-white px-4 font-medium text-gray-900 ring-1 ring-inset ring-black/[0.08] transition-colors hover:bg-gray-50 motion-reduce:transition-none",
                        TAP_TARGET,
                        "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600",
                    )}
                >
                    <PaperclipIcon aria-hidden size={14} />
                    Add a photo or file
                </label>
                <p className={cn(TYPE.tm, "m-0 mt-1.5 text-gray-500")}>JPG, PNG, WebP or PDF, up to 20 MB each.</p>
            </div>

            {files.length > 0 && (
                <ul aria-label="Files to send" className="list-plain m-0 mt-3 space-y-2 p-0">
                    {files.map((f) => (
                        <li key={f.id} className="flex min-w-0 items-center gap-2 rounded-xl bg-gray-50 py-1 pl-3 pr-1">
                            {f.fileKey === null ? (
                                <Loader2Icon aria-hidden size={14} className="shrink-0 animate-spin text-gray-400 motion-reduce:animate-none" />
                            ) : f.image ? (
                                <ImageIcon aria-hidden size={14} className="shrink-0 text-blue-600" />
                            ) : (
                                <FileTextIcon aria-hidden size={14} className="shrink-0 text-red-600" />
                            )}
                            <span title={f.name} className={cn(TYPE.tc, "min-w-0 flex-1 truncate text-gray-900")}>
                                {f.name}
                            </span>
                            <span className={cn(TYPE.tm, "shrink-0 tabular-nums text-gray-500")}>{f.fileKey === null ? "Uploading" : sizeText(f.size)}</span>
                            <button
                                type="button"
                                onClick={() => onRemove(f.id)}
                                aria-label={`Remove ${f.name}`}
                                className={cn(
                                    "relative grid h-8 w-8 shrink-0 place-items-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900",
                                    "after:absolute after:-inset-1.5 after:content-['']",
                                    FOCUS_RING,
                                )}
                            >
                                <XIcon aria-hidden size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {fileProblems.length > 0 && (
                <div role="alert" className="mt-2 space-y-1">
                    {fileProblems.map((p, i) => (
                        <p key={i} className={cn(TYPE.tc, LATE_TEXT, "m-0 break-words")}>
                            {p}
                        </p>
                    ))}
                </div>
            )}

            {error && (
                <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                    {error}
                </p>
            )}

            {smallChangeLine && <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>{smallChangeLine}</p>}
        </DrawerBlock>
    );
}
